// Small shared helpers for the Zero demo fixtures (pure: no services, no stores).

import {
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  materialById,
  categoryByCode,
  naiveIso,
  round,
} from '../core';
import { OrganizationType } from '../../types/organization.type';

/** Naive UTC timestamp ('2026-10-06T08:14:00') — how the Zero API serialises datetimes. */
export function naive(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : naiveIso(d);
}

/** Like {@link naive} but never null (for required fields). */
export function naiveRequired(value: string): string {
  return naive(value) ?? value;
}

/** Epoch ms of an API timestamp; naive strings are UTC. */
export function toMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const d = new Date(hasZone || !s.includes('T') ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** 'YYYY-MM' of an ISO / naive timestamp (UTC calendar, like the API buckets). */
export function monthOf(value: string): string {
  return value.slice(0, 7);
}

/** 'YYYY-MM' of `offset` months before `key` ('2026-10', -1 -> '2026-09'). */
export function shiftMonth(key: string, offset: number): string {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + offset, 1));
  return d.toISOString().slice(0, 7);
}

/** The last `count` month keys, oldest first, ending with the current UTC month. */
export function lastMonthKeys(count = 12): string[] {
  const now = new Date();
  const current = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  return Array.from({ length: count }, (_, i) => shiftMonth(current, i - (count - 1)));
}

/**
 * Month key from a dashboard query: `month_date` ('2026-10-01T00:00:00Z' built
 * from the local calendar), `year` + `month`, or none (current month).
 */
export function monthFromQuery(query: Record<string, unknown>): string {
  const raw = query.month_date;
  const md = Array.isArray(raw) ? raw[0] : raw;
  if (typeof md === 'string' && /^\d{4}-\d{2}/.test(md)) return md.slice(0, 7);
  const year = Number(query.year);
  const month = Number(query.month);
  if (Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12) {
    return `${year}-${String(month).padStart(2, '0')}`;
  }
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function isTruthyParam(value: unknown): boolean {
  const v = Array.isArray(value) ? value[0] : value;
  return v === true || v === 'true' || v === '1' || v === 1;
}

export function orgTypeFor(orgId: string | null | undefined): OrganizationType | null {
  if (orgId === DEMO_OUTLET_ORG_ID) return OrganizationType.WASTE_OUTLET;
  if (orgId === DEMO_RECYCLER_ORG_ID) return OrganizationType.RECYCLER;
  return null;
}

export function materialName(materialId: string): string {
  return materialById(materialId)?.name_en ?? 'Other';
}

/** Catalog category code of a material ('cardboard', 'plastic' …). */
export function materialCategory(materialId: string): string {
  return materialById(materialId)?.category_code ?? 'mixed';
}

export function categoryName(code: string): string {
  return categoryByCode(code)?.name_en ?? code;
}

/** Share of collected material that leaves the sorting line as recyclate. */
const RECOVERY_BY_CATEGORY: Record<string, number> = {
  cardboard: 0.96,
  paper: 0.93,
  plastic: 0.89,
  metal: 0.98,
  glass: 0.95,
  oil: 0.97,
  wood: 0.9,
};

export function recoveryShare(materialId: string): number {
  return RECOVERY_BY_CATEGORY[materialCategory(materialId)] ?? 0.85;
}

export function pct(part: number, whole: number, decimals = 1): number {
  return whole > 0 ? round((part / whole) * 100, decimals) : 0;
}

export function sum<T>(items: readonly T[], pick: (item: T) => number): number {
  let total = 0;
  for (const item of items) total += pick(item);
  return total;
}

/** Tiny deterministic jitter (degrees) so markers at one site don't stack. */
export function jitter(index: number, step = 0.00022): { dLat: number; dLng: number } {
  const ring = Math.floor(index / 8) + 1;
  const angle = (index % 8) * (Math.PI / 4);
  return { dLat: Math.sin(angle) * step * ring, dLng: Math.cos(angle) * step * ring };
}

export function splitName(full: string): { first_name: string; last_name: string } {
  const [first, ...rest] = full.split(' ');
  return { first_name: first, last_name: rest.join(' ') };
}
